import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Search,
  Building2,
  Users,
  HardHat,
  Briefcase,
  ChevronRight,
  Plus,
  Star,
  Activity,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  Phone,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Filter,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Checkbox } from '../../components/ui/checkbox';

// Churn-risk colour cue for Last Activity: amber > 14d, red > 30d, grey if never
const lastActivityColor = (iso, isDark) => {
  if (!iso) return isDark ? 'text-slate-500' : 'text-gray-400';
  const days = (Date.now() - new Date(iso).getTime()) / 86400000;
  if (days > 30) return 'text-red-500';
  if (days > 14) return 'text-amber-500';
  return isDark ? 'text-slate-300' : 'text-gray-700';
};

const INDUSTRY_META = {
  crane_hire: { label: 'Crane Hire', cls: 'bg-blue-500/20 text-blue-600 border-blue-500/40' },
  earthmoving: { label: 'Earthmoving', cls: 'bg-orange-500/20 text-orange-600 border-orange-500/40' },
  haulage: { label: 'Haulage', cls: 'bg-green-500/20 text-green-600 border-green-500/40' },
  concrete_pumping: { label: 'Concrete Pumping', cls: 'bg-sky-500/20 text-sky-600 border-sky-500/40' },
  street_sweeping: { label: 'Street Sweeping', cls: 'bg-emerald-500/20 text-emerald-600 border-emerald-500/40' },
};
const getIndustryMeta = (industry) =>
  INDUSTRY_META[industry] || {
    label: industry ? industry.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'General',
    cls: 'bg-slate-500/20 text-slate-500 border-slate-500/40',
  };

const TenantsList = () => {
  const navigate = useNavigate();
  const { api, admin } = useSuperAdmin();
  const { isDark = true } = useOutletContext() || {};
  // Support agents must not see monetary figures (MRR).
  const showMRR = admin?.role !== 'support';
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const ALL_STATUSES = ['active', 'onboarding', 'trialing', 'grace_period', 'past_due', 'suspended', 'cancelled'];
  const DEFAULT_STATUSES = new Set(ALL_STATUSES.filter(s => s !== 'cancelled'));
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUSES);
  const [sortKey, setSortKey] = useState('last_activity_at');
  const [sortDir, setSortDir] = useState('asc');

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SORT_ACCESSORS = {
    company_name: (t) => (t.company_name || '').toLowerCase(),
    industry: (t) => getIndustryMeta(t.industry).label.toLowerCase(),
    tier: (t) => t.tier || 'basic',
    subscription_status: (t) => t.subscription_status || 'active',
    users_count: (t) => t.users_count || 0,
    operators_count: (t) => t.operators_count || 0,
    jobs_count: (t) => t.jobs_count || 0,
    last_activity_at: (t) => (t.last_activity_at ? new Date(t.last_activity_at).getTime() : 0),
    created_at: (t) => (t.created_at ? new Date(t.created_at).getTime() : 0),
    mrr: (t) => (typeof t.mrr === 'number' ? t.mrr : -1),
    jobs_this_week: (t) => (t.jobs_this_week || 0),
  };

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const res = await api.get('/api/super-admin/tenants');
        setTenants(res.data);
      } catch (err) {
        console.error('Failed to fetch tenants:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTenants();
  }, [api]);

  const filteredTenants = tenants.filter(t => {
    const matchesSearch = 
      t.company_name?.toLowerCase().includes(search.toLowerCase()) ||
      t.client_id?.toLowerCase().includes(search.toLowerCase());
    const matchesTier = tierFilter === 'all' || t.tier === tierFilter;
    const tenantStatus = t.subscription_status || 'active';
    const matchesStatus = statusFilter.has(tenantStatus);
    return matchesSearch && matchesTier && matchesStatus;
  });

  const sortedTenants = [...filteredTenants].sort((a, b) => {
    const acc = SORT_ACCESSORS[sortKey] || SORT_ACCESSORS.last_activity_at;
    const av = acc(a);
    const bv = acc(b);
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Real (paying) tenants at the top; Test tenants grouped at the bottom.
  const realTenants = sortedTenants.filter(t => !t.is_test_tenant);
  const testTenants = sortedTenants.filter(t => t.is_test_tenant);

  const toggleTestTenant = async (tenant, e) => {
    e.stopPropagation();
    const next = !tenant.is_test_tenant;
    // optimistic update
    setTenants(prev => prev.map(t => t.client_id === tenant.client_id ? { ...t, is_test_tenant: next } : t));
    try {
      await api.put(`/api/super-admin/tenants/${tenant.client_id}`, { is_test_tenant: next });
    } catch (err) {
      // revert on failure
      setTenants(prev => prev.map(t => t.client_id === tenant.client_id ? { ...t, is_test_tenant: !next } : t));
    }
  };

  const columnCount = showMRR ? 12 : 11;

  const getTierColor = (tier) => {
    switch (tier) {
      case 'professional': return 'bg-[#ffd108] text-black';
      case 'advanced': return 'bg-blue-500 text-white';
      default: return 'bg-slate-500 text-white';
    }
  };

  const formatStatusLabel = (status) => {
    const labels = {
      active: 'Active', onboarding: 'On-Boarding', trialing: 'Trialling', grace_period: 'Grace Period',
      past_due: 'Past Due', suspended: 'Suspended', cancelled: 'Cancelled',
    };
    return labels[status] || status || 'Active';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700';
      case 'onboarding': return isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700';
      case 'trialing': return isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700';
      case 'grace_period': return isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700';
      case 'past_due': return isDark ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-700';
      case 'suspended': return isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700';
      case 'cancelled': return isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700';
      default: return isDark ? 'bg-slate-500/20 text-slate-400' : 'bg-gray-100 text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  const headClass = isDark ? 'text-slate-400' : 'text-gray-500';

  const mrrCurrency = tenants.find(t => t.mrr_currency)?.mrr_currency || 'AUD';
  const formatMRR = (amount, currency = mrrCurrency) => {
    if (amount === null || amount === undefined) return '—';
    try {
      return new Intl.NumberFormat('en-AU', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
    } catch {
      return `$${Math.round(amount).toLocaleString()}`;
    }
  };
  const totalMRR = tenants.reduce((sum, t) => {
    if ((t.subscription_status || 'active') !== 'active') return sum;
    return sum + (typeof t.mrr === 'number' ? t.mrr : 0);
  }, 0);

  const SortHead = ({ label, sortKey: key, className = '', icon = null }) => (
    <TableHead className={`${headClass} ${className}`}>
      <button
        type="button"
        onClick={() => toggleSort(key)}
        className={`inline-flex items-center gap-1 hover:text-[#ffd108] transition-colors ${className.includes('text-center') ? 'mx-auto' : ''}`}
        data-testid={`tenant-sort-${key}`}
      >
        {icon || label}
        {sortKey === key
          ? (sortDir === 'asc' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />)
          : <ArrowUpDown className="h-3 w-3 opacity-40" />}
      </button>
    </TableHead>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Tenants</h1>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>
            {tenants.length} total tenants
            {showMRR && (
              <>
                <span className={isDark ? 'text-slate-500' : 'text-gray-400'}> · </span>
                <span className="font-semibold text-[#ffd108]" data-testid="tenants-total-mrr">{formatMRR(totalMRR)}/mo</span>
                <span className={isDark ? 'text-slate-400' : 'text-gray-500'}> total MRR</span>
              </>
            )}
          </p>
        </div>
        <Button 
          onClick={() => navigate('/super-admin/tenants/new')}
          className="bg-[#ffd108] text-black hover:bg-[#e6bc07]"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Tenant
        </Button>
      </div>

      {/* Filters */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
              <Input
                placeholder="Search by company or client ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`pl-10 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
              />
            </div>
            <Select value={tierFilter} onValueChange={setTierFilter}>
              <SelectTrigger className={`w-full sm:w-40 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}>
                <SelectValue placeholder="Filter by tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
                <SelectItem value="professional">Professional</SelectItem>
              </SelectContent>
            </Select>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="status-filter" className={`w-full sm:w-52 justify-between font-normal ${isDark ? 'bg-slate-700/50 border-slate-600 text-white hover:bg-slate-700' : 'bg-gray-50 border-gray-200 text-gray-900 hover:bg-gray-100'}`}>
                  <span className="flex items-center gap-1.5 truncate">
                    <Filter className="h-3.5 w-3.5 shrink-0 opacity-60" />
                    {statusFilter.size === ALL_STATUSES.length
                      ? 'All Statuses'
                      : statusFilter.size === 0
                        ? 'No Status'
                        : statusFilter.size === 1
                          ? formatStatusLabel([...statusFilter][0])
                          : ALL_STATUSES.length - statusFilter.size === 1
                            ? `All except ${formatStatusLabel(ALL_STATUSES.find(s => !statusFilter.has(s)))}`
                            : `${statusFilter.size} statuses`}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className={`w-52 p-1.5 ${isDark ? 'bg-slate-800 border-slate-700' : ''}`}>
                {[
                  { value: 'active', label: 'Active' },
                  { value: 'onboarding', label: 'On-Boarding' },
                  { value: 'trialing', label: 'Trialling' },
                  { value: 'grace_period', label: 'Grace Period' },
                  { value: 'past_due', label: 'Past Due' },
                  { value: 'suspended', label: 'Suspended' },
                  { value: 'cancelled', label: 'Cancelled' },
                ].map(opt => (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm ${isDark ? 'hover:bg-slate-700 text-gray-200' : 'hover:bg-gray-100 text-gray-800'}`}
                  >
                    <Checkbox
                      checked={statusFilter.has(opt.value)}
                      onCheckedChange={(checked) => {
                        setStatusFilter(prev => {
                          const next = new Set(prev);
                          checked ? next.add(opt.value) : next.delete(opt.value);
                          return next;
                        });
                      }}
                    />
                    {opt.label}
                  </label>
                ))}
                {statusFilter.size < ALL_STATUSES.length && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full mt-1 text-xs"
                    onClick={() => setStatusFilter(new Set(ALL_STATUSES))}
                  >
                    Select All
                  </Button>
                )}
                {statusFilter.size > 0 && statusFilter.size !== DEFAULT_STATUSES.size && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs"
                    onClick={() => setStatusFilter(new Set(DEFAULT_STATUSES))}
                  >
                    Reset
                  </Button>
                )}
              </PopoverContent>
            </Popover>
          </div>
        </CardContent>
      </Card>

      {/* Tenants Table */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className={isDark ? 'border-slate-700 hover:bg-transparent' : 'border-gray-200 hover:bg-transparent'}>
                <SortHead label="Company" sortKey="company_name" />
                <SortHead label="Industry" sortKey="industry" />
                <SortHead label="Tier" sortKey="tier" />
                <SortHead label="Status" sortKey="subscription_status" />
                {showMRR && <SortHead label="MRR" sortKey="mrr" className="text-right" />}
                <SortHead label="Last Activity" sortKey="last_activity_at" />
                <SortHead label="Jobs (7d)" sortKey="jobs_this_week" />
                <SortHead sortKey="users_count" className="text-center" icon={<Users className="h-4 w-4" />} />
                <SortHead sortKey="operators_count" className="text-center" icon={<HardHat className="h-4 w-4" />} />
                <SortHead sortKey="jobs_count" className="text-center" icon={<Briefcase className="h-4 w-4" />} />
                <SortHead label="Created" sortKey="created_at" />
                <TableHead className={headClass}></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                const renderRow = (tenant) => (
                <TableRow
                  key={tenant.client_id}
                  className={`${isDark ? 'border-slate-700 hover:bg-slate-700/30' : 'border-gray-100 hover:bg-gray-50'} cursor-pointer`}
                  onClick={() => navigate(`/super-admin/tenants/${tenant.client_id}`)}
                  data-testid={`tenant-row-${tenant.client_id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className={`p-2 ${isDark ? 'bg-slate-700' : 'bg-gray-100'} rounded-lg`}>
                        <Building2 className="h-4 w-4 text-[#ffd108]" />
                      </div>
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {tenant.company_name}
                          {tenant.early_adopter && (
                            <Star className="inline h-3.5 w-3.5 ml-1.5 text-[#ffd108] fill-[#ffd108] -mt-0.5" />
                          )}
                          {tenant.phone_support && (
                            <Phone className="inline h-3.5 w-3.5 ml-1.5 text-emerald-500 -mt-0.5" data-testid={`tenant-phone-support-${tenant.client_id}`} title="Phone support enabled" />
                          )}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{tenant.client_id}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={getIndustryMeta(tenant.industry).cls} data-testid={`tenant-industry-${tenant.client_id}`}>
                      {getIndustryMeta(tenant.industry).label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={getTierColor(tenant.tier)}>
                      {tenant.tier || 'basic'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(tenant.subscription_status)}>
                      {formatStatusLabel(tenant.subscription_status)}
                    </Badge>
                  </TableCell>
                  {showMRR && (
                  <TableCell className="text-right" data-testid={`tenant-mrr-${tenant.client_id}`}>
                    <div className="flex flex-col items-end leading-tight">
                      <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {formatMRR(tenant.mrr, tenant.mrr_currency)}
                      </span>
                      {tenant.mrr_source === 'estimated' && (
                        <span className="text-[10px] uppercase tracking-wide text-amber-500 font-medium">Est.</span>
                      )}
                      {tenant.mrr_source === 'calculated' && (
                        <span className={`text-[10px] uppercase tracking-wide ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Calc</span>
                      )}
                    </div>
                  </TableCell>
                  )}
                  <TableCell className={`text-sm font-medium ${lastActivityColor(tenant.last_activity_at, isDark)}`} data-testid={`tenant-last-activity-${tenant.client_id}`}>
                    <span className="inline-flex items-center gap-1.5">
                      <Activity className="h-3.5 w-3.5" />
                      {tenant.last_activity_at
                        ? formatDistanceToNow(new Date(tenant.last_activity_at), { addSuffix: true })
                        : 'No activity'}
                    </span>
                  </TableCell>
                  <TableCell data-testid={`tenant-jobs-trend-${tenant.client_id}`}>
                    {(() => {
                      const tw = tenant.jobs_this_week || 0;
                      const lw = tenant.jobs_last_week || 0;
                      let cls, Icon, label;
                      if (tw === 0) { cls = 'text-red-500'; Icon = AlertTriangle; label = 'At risk'; }
                      else if (tw < lw) { cls = 'text-amber-500'; Icon = TrendingDown; label = null; }
                      else { cls = 'text-emerald-500'; Icon = TrendingUp; label = null; }
                      return (
                        <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${cls}`}>
                          <Icon className="h-3.5 w-3.5" />
                          {tw}
                          {label && <span className="text-[10px] uppercase tracking-wide">{label}</span>}
                          <span className={`text-[11px] font-normal ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>(was {lw})</span>
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell className={`text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>{tenant.users_count}</TableCell>
                  <TableCell className={`text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>{tenant.operators_count}</TableCell>
                  <TableCell className={`text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>{tenant.jobs_count}</TableCell>
                  <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    {tenant.created_at
                      ? formatDistanceToNow(new Date(tenant.created_at), { addSuffix: true })
                      : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={(e) => toggleTestTenant(tenant, e)}
                        title={tenant.is_test_tenant ? 'Unmark as test tenant' : 'Mark as test tenant'}
                        data-testid={`toggle-test-tenant-${tenant.client_id}`}
                        className={`px-2 py-1 rounded text-[10px] font-semibold uppercase tracking-wide border transition-colors ${
                          tenant.is_test_tenant
                            ? 'border-amber-500 text-amber-600 bg-amber-500/10 hover:bg-amber-500/20'
                            : (isDark ? 'border-slate-600 text-slate-400 hover:bg-slate-700' : 'border-gray-300 text-gray-500 hover:bg-gray-100')
                        }`}
                      >
                        {tenant.is_test_tenant ? 'Test ✓' : 'Mark Test'}
                      </button>
                      <ChevronRight className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                    </div>
                  </TableCell>
                </TableRow>
                );
                return (
                  <>
                    {realTenants.map(renderRow)}
                    {testTenants.length > 0 && (
                      <TableRow className={isDark ? 'bg-slate-900/60 hover:bg-slate-900/60' : 'bg-gray-100 hover:bg-gray-100'}>
                        <TableCell colSpan={columnCount} className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-slate-300' : 'text-gray-600'}`} data-testid="test-tenants-divider">
                          Test Tenants ({testTenants.length})
                        </TableCell>
                      </TableRow>
                    )}
                    {testTenants.map(renderRow)}
                  </>
                );
              })()}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantsList;
